import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api";
import { ApiResponse } from "../types";
import { getErrorMessage } from "../utils/apiError";

/**
 * GETs `url` (with optional query params) and returns { data, loading, error, reload }.
 * Pass url = null to skip the request.
 */
export function useApi<T>(url: string | null, params?: Record<string, string | number | undefined>) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState<boolean>(!!url);
    const [error, setError] = useState<string>("");

    // Stable key so a new params object with the same values doesn't refetch
    const paramsKey = JSON.stringify(params ?? {});
    const requestId = useRef(0);

    const load = useCallback(async () => {
        if (!url) {
            setLoading(false);
            return;
        }

        const id = ++requestId.current;
        setLoading(true);
        setError("");

        try {
            const cleanParams = Object.fromEntries(
                Object.entries(JSON.parse(paramsKey)).filter(([, v]) => v !== undefined && v !== "")
            );
            const res = await api.get<ApiResponse<T>>(url, { params: cleanParams });
            if (id === requestId.current) setData(res.data.data);
        } catch (err) {
            if (id === requestId.current) setError(getErrorMessage(err, "Failed to load data"));
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [url, paramsKey]);

    useEffect(() => {
        load();
    }, [load]);

    return { data, loading, error, reload: load, setData };
}